import React, { useState } from 'react';
import {
    LayoutDashboard,
    BarChart2,
    Users,
    FolderOpen,
    CheckSquare,
    Search,
    Settings,
    Bell,
    LogOut,
    ChevronRight
} from 'lucide-react';

// Common Types
type PageProps = {
    activePage: string;
};

// --- Data for each page ---
const pageContent: Record<string, { title: string; description: string; content: React.ReactNode }> = {
    Dashboard: {
        title: 'Dashboard',
        description: "Welcome back, Serafim. Here's what's happening today.",
        content: (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                    <h2 className="text-lg font-semibold text-white">Active Projects</h2>
                    <p className="text-4xl font-bold mt-2 text-indigo-400">12</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                    <h2 className="text-lg font-semibold text-white">Tasks Due</h2>
                    <p className="text-4xl font-bold mt-2 text-pink-400">5</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300">
                    <h2 className="text-lg font-semibold text-white">New Users</h2>
                    <p className="text-4xl font-bold mt-2 text-emerald-400">28</p>
                </div>
            </div>
        )
    },
    Analytics: {
        title: 'Analytics',
        description: 'Detailed insights and metrics for your projects.',
        content: (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 lg:col-span-2 h-64 flex items-center justify-center">
                    <p className="text-gray-400">Chart placeholder for User Growth</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-white">Bounce Rate</h2>
                    <p className="text-4xl font-bold mt-2 text-indigo-400">24.5%</p>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-white">Session Duration</h2>
                    <p className="text-4xl font-bold mt-2 text-pink-400">8m 12s</p>
                </div>
            </div>
        )
    },
    Users: {
        title: 'Users',
        description: 'Manage all the users in your organization.',
        content: (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-300">
                        <thead className="bg-white/5 text-xs uppercase font-medium">
                            <tr>
                                <th className="px-6 py-4">Name</th>
                                <th className="px-6 py-4">Email</th>
                                <th className="px-6 py-4">Role</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                            <tr>
                                <td className="px-6 py-4">Jane Doe</td>
                                <td className="px-6 py-4">jane.doe@example.com</td>
                                <td className="px-6 py-4"><span className="bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded text-xs">Admin</span></td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4">John Smith</td>
                                <td className="px-6 py-4">john.smith@example.com</td>
                                <td className="px-6 py-4"><span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded text-xs">Developer</span></td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4">Sam Wilson</td>
                                <td className="px-6 py-4">sam.wilson@example.com</td>
                                <td className="px-6 py-4"><span className="bg-pink-500/20 text-pink-300 px-2 py-1 rounded text-xs">Designer</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        )
    },
    Projects: {
        title: 'Projects',
        description: 'An overview of all your ongoing and completed projects.',
        content: (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-white">Project Alpha</h2>
                    <p className="text-sm text-gray-400 mt-1 mb-4">Aether UI Redesign</p>
                    <div className="w-full bg-white/10 rounded-full h-1.5 mb-2">
                        <div className="bg-indigo-400 h-1.5 rounded-full w-2/3"></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                        <span>Progress</span>
                        <span>60%</span>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                    <h2 className="text-lg font-semibold text-white">Project Beta</h2>
                    <p className="text-sm text-gray-400 mt-1 mb-4">Mobile App Launch</p>
                    <div className="w-full bg-white/10 rounded-full h-1.5 mb-2">
                        <div className="bg-emerald-400 h-1.5 rounded-full w-full"></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                        <span>Status</span>
                        <span className="text-emerald-400">Completed</span>
                    </div>
                </div>
            </div>
        )
    },
    Tasks: {
        title: 'Tasks',
        description: 'Track and manage all your tasks and to-dos.',
        content: (
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
                <ul className="space-y-4">
                    <li className="flex items-center justify-between pb-4 border-b border-white/10 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded border border-pink-400/50 flex items-center justify-center"></div>
                            <span className="text-gray-200">Finalize Q3 report</span>
                        </div>
                        <span className="text-xs text-pink-400 bg-pink-400/10 px-2 py-1 rounded">Due Tomorrow</span>
                    </li>
                    <li className="flex items-center justify-between pb-4 border-b border-white/10 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded border border-gray-400/50 flex items-center justify-center"></div>
                            <span className="text-gray-200">Design new landing page mockups</span>
                        </div>
                        <span className="text-xs text-indigo-400 bg-indigo-400/10 px-2 py-1 rounded">In Progress</span>
                    </li>
                    <li className="flex items-center justify-between pb-4 border-b border-white/10 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded bg-emerald-500/20 border border-emerald-500 flex items-center justify-center text-emerald-500 text-xs">✓</div>
                            <span className="text-gray-200 line-through text-gray-500">Deploy server updates</span>
                        </div>
                        <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">Completed</span>
                    </li>
                </ul>
            </div>
        )
    }
};

const navItems = [
    { page: 'Dashboard', icon: LayoutDashboard },
    { page: 'Analytics', icon: BarChart2 },
    { page: 'Users', icon: Users },
    { page: 'Projects', icon: FolderOpen },
    { page: 'Tasks', icon: CheckSquare },
];

// Sidebar Component
interface SidebarProps {
    activePage: string;
    setActivePage: (page: string) => void;
}

const Sidebar = ({ activePage, setActivePage }: SidebarProps) => (
    <aside className="w-64 flex-shrink-0 flex flex-col z-20 relative backdrop-blur-xl bg-white/5 border-r border-white/10 transition-all duration-500">
        {/* Logo Area */}
        <div className="h-24 flex items-center px-8 border-b border-white/10">
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                    <span className="font-bold text-white text-lg">A</span>
                </div>
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">AetherUI</span>
            </div>
        </div>

        {/* Navigation */}
        <nav className="flex-grow p-4 space-y-2 overflow-y-auto">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 px-4 mt-2">Menu</div>
            {navItems.map(item => {
                const Icon = item.icon;
                const isActive = activePage === item.page;
                return (
                    <button
                        key={item.page}
                        onClick={() => setActivePage(item.page)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 relative group overflow-hidden ${isActive
                                ? 'bg-white/10 text-white shadow-lg border border-white/10'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`}
                    >
                        {isActive && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-pink-500 rounded-l-xl"></div>
                        )}
                        <Icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? 'scale-110 text-indigo-400' : 'group-hover:scale-110'}`} />
                        <span className="font-medium">{item.page}</span>
                        {isActive && <ChevronRight className="w-4 h-4 ml-auto text-gray-500" />}
                    </button>
                );
            })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-white/10 bg-white/5 backdrop-blur-sm">
            <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors cursor-pointer group">
                <div className="relative">
                    <img src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=150&auto=format&fit=crop" alt="User Avatar" className="w-10 h-10 rounded-full border-2 border-indigo-400/50 object-cover" />
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-[#1a1a2e]"></div>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-white truncate">Serafim P.</p>
                    <p className="text-xs text-gray-400 truncate">Admin</p>
                </div>
                <LogOut className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </div>
    </aside>
);

// Main Content Component
const MainContent = ({ activePage }: PageProps) => {
    const { title, description, content } = pageContent[activePage];
    return (
        <main className="flex-grow p-8 md:p-12 overflow-y-auto relative z-10 w-full">
            {/* Header */}
            <header className="flex items-center justify-between mb-12">
                <div>
                    <h1 className="text-4xl font-bold text-white tracking-tight">{title}</h1>
                    <p className="text-gray-400 mt-2 text-lg">{description}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                        <Search className="w-5 h-5" />
                    </button>
                    <button className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors relative">
                        <Bell className="w-5 h-5" />
                        <span className="absolute top-2 right-2.5 w-2 h-2 bg-pink-500 rounded-full"></span>
                    </button>
                </div>
            </header>


            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {content}
            </div>
        </main>
    );
};

// Background Component
const Background = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Dark noise texture or gradient base */}
        <div className="absolute inset-0 bg-[#0f172a]" /> {/* Slate-900 base */}

        {/* Animated Shapes */}
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-600/20 blur-[100px] animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute top-[20%] right-[10%] w-[40%] h-[40%] rounded-full bg-pink-600/20 blur-[120px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }}></div>
        <div className="absolute bottom-[10%] left-[20%] w-[30%] h-[30%] rounded-full bg-emerald-600/10 blur-[100px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '4s' }}></div>
    </div>
)

// Main Dashboard Layout Component
export const DashboardLayout = () => {
    const [activePage, setActivePage] = useState('Dashboard');
    return (
        <div className="relative min-h-screen w-full flex bg-gray-950 font-sans selection:bg-indigo-500/30">
            <Background />
            <Sidebar activePage={activePage} setActivePage={setActivePage} />
            <MainContent activePage={activePage} />
        </div>
    );
};

export default DashboardLayout;
